import type { RealEstateContact } from "../../@types/sharedSchema";
import type { CrmContactRef, CrmContactSelection } from "../../@types/webotServer";

/**
 * THE LEGACY CONTACTS BOOK — the pure half of keeping `useLegacyContactsBook().allContacts` correct.
 *
 * `allContacts` is the realtor's whole book, downloaded ONCE per session and then never
 * refetched. It survives only for the two legacy whole-book tables (the old contacts page
 * and the extension-send wizard's grid); every other surface reads the server.
 *
 * For years it stayed fresh by accident: every contact write went through
 * `updateContact` / `batchUpdate`, which patched the array as a side effect. The moment a
 * write moved to a server endpoint — the create dialog and the file import did, on
 * 2026-09-15 — nothing inserted the new contact any more, and the old page stopped
 * showing contacts until the browser was reloaded (Trello #207). Contacts created by a
 * call analysis or by the AI assistant never reached it at all.
 *
 * So the array now has exactly three ways to change, and they are all here:
 *
 *  - {@link mergeIntoBook}   — a DOCUMENT arrived (the live listener). It is the truth.
 *  - {@link upsertIntoBook}  — a write we just made, known only as the fields we sent.
 *  - {@link removeFromBook}  — a delete we just made.
 *
 * Everything in this file is pure, so the rules can be tested without Firebase (the
 * repo's jest cannot even import a context — see `ContactQuickActionsMenu.test.tsx`).
 */

/** Stamps `_metadata` and the read-side fallbacks. `postProcessContact`, injected. */
export type BookPostProcessor = (contact: RealEstateContact, email: string) => RealEstateContact;

/**
 * A Firestore document, in the shape the one-shot download would have delivered it.
 *
 * ── Why a JSON round trip ────────────────────────────────────────────────────────
 *
 * The download arrives as JSON, so every date in the array is an ISO STRING; the listener's
 * converter hands back `Date` objects. The old table sorts on `updatedTime` with a plain
 * `<`, and a `Date` compared against a string sorts the row arbitrarily — so a merged
 * document has to look exactly like a downloaded one, for every date in it, at any depth.
 *
 * It is also the clone: `postProcessContact` MUTATES its argument, and the object handed in
 * here belongs to the Firestore SDK's snapshot cache.
 */
export function normaliseBookContact(
  raw: RealEstateContact,
  email: string,
  postProcess: BookPostProcessor
): RealEstateContact {
  return postProcess(JSON.parse(JSON.stringify(raw)) as RealEstateContact, email);
}

/**
 * Whole documents replace what the book holds; unknown ids are PREPENDED.
 *
 * Prepending is the point of the whole bridge: the listener the old table used to own
 * only ever patched rows that already existed, so a contact created anywhere else arrived
 * and was dropped. Here it becomes a row.
 *
 * A full replace rather than a field merge, because `docs` are documents: a field the
 * realtor CLEARED is simply absent from the new one, and merging would resurrect it.
 */
export function mergeIntoBook(
  book: RealEstateContact[],
  docs: RealEstateContact[]
): RealEstateContact[] {
  if (docs.length === 0) return book;

  const incoming = new Map(docs.map((doc) => [doc.id, doc]));
  const merged = book.map((contact) => {
    const replacement = incoming.get(contact.id);
    if (!replacement) return contact;
    incoming.delete(contact.id);
    return replacement;
  });

  // What is left was not in the book. On top, where a realtor looks for something new —
  // the table re-sorts anyway, and the pickers show the array in this order.
  return [...Array.from(incoming.values()), ...merged];
}

/**
 * A write we made ourselves, known only as the fields we sent.
 *
 * A FIELD merge, unlike {@link mergeIntoBook}: the caller holds a patch, not a document,
 * so everything it did not send must survive.
 *
 * ── Both timestamps are stamped on a NEW contact, and that is load-bearing ────────
 *
 * The old table's default sort is `updatedTime desc`, and its comparator turns a missing
 * value into `""` — so a just-created contact with no `updatedTime` sorts LAST, behind
 * thousands of rows, and the realtor concludes the create did not work. (The Cloud
 * Functions stamp the real values a few seconds later, and the listener replaces these.)
 */
export function upsertIntoBook(
  book: RealEstateContact[],
  partials: Partial<RealEstateContact>[],
  nowIso: string,
  toNewContact: (partial: Partial<RealEstateContact>) => RealEstateContact
): RealEstateContact[] {
  const incoming = new Map<string, Partial<RealEstateContact>>();
  partials.forEach((partial) => {
    if (partial.id) incoming.set(partial.id, { ...incoming.get(partial.id), ...partial });
  });
  if (incoming.size === 0) return book;

  // The cast names the same lie `AppContext` documents: these two fields are typed `Date`
  // and hold ISO strings everywhere in this array.
  const stamp = nowIso as unknown as Date;

  const patched = book.map((contact) => {
    const patch = incoming.get(contact.id);
    if (!patch) return contact;
    incoming.delete(contact.id);
    return { ...contact, ...patch, updatedTime: stamp } as RealEstateContact;
  });

  const created = Array.from(incoming.values()).map((partial) =>
    toNewContact({ createdTime: stamp, updatedTime: stamp, ...partial })
  );

  return [...created, ...patched];
}

/** Drops the given ids. Returns the SAME array when none of them was in the book. */
export function removeFromBook(book: RealEstateContact[], ids: Iterable<string>): RealEstateContact[] {
  const doomed = new Set(ids);
  if (doomed.size === 0) return book;
  const kept = book.filter((contact) => !doomed.has(contact.id));
  return kept.length === book.length ? book : kept;
}

/**
 * Which of a bulk selection's contacts live in MY book — or `null` when that cannot be known.
 *
 * `null` is the honest answer for a `filter` selection: the server resolved the set, the
 * browser never saw the ids, and the only correct reaction is to stop trusting the book.
 *
 * A bare string and a ref without an owner both mean "mine" — the same default the server
 * applies. A ref naming a colleague is skipped: the agency view can put their contacts on
 * screen, and their book is not this one.
 */
export function ownContactIdsOfSelection(
  selection: CrmContactSelection,
  myUserId: string
): string[] | null {
  if (selection.mode !== "ids") return null;

  const mine = myUserId.trim().toLowerCase();
  return selection.contacts
    .filter((ref: CrmContactRef | string) => {
      if (typeof ref === "string") return true;
      return !ref.ownerUserId || ref.ownerUserId.trim().toLowerCase() === mine;
    })
    .map((ref) => (typeof ref === "string" ? ref : ref.contactId));
}
